import React, { useMemo, useState } from 'react';
import { adminCreateUser, adminUpdateUser, adminDeleteUser, adminKillUserSessions } from '../../api/admin';
import type { SafeUser, Company } from '../../types/auth';
import { useAuthStore } from '../../store/useAuthStore';
import { useToastStore } from '../../store/useToastStore';
import { TH, usePaginated, PaginationBar, ConfirmDialog } from './shared';
import UserEditor from './UserEditor';
import type { UserSaveData } from './UserEditor';

interface Props {
  users: SafeUser[];
  companies: Company[];
  onChanged: () => Promise<void>;
}

export default function UsersTab({ users, companies, onChanged }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const currentUser = useAuthStore((s) => s.user);
  const [editing, setEditing] = useState<SafeUser | null | 'new'>(null);
  const [confirmDelete, setConfirmDelete] = useState<SafeUser | null>(null);
  const [searchQ, setSearchQ] = useState('');

  const filtered = useMemo(() => {
    const q = searchQ.toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.displayName.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      (companies.find((c) => c.id === u.companyId)?.name ?? '').toLowerCase().includes(q),
    );
  }, [users, searchQ, companies]);

  const pag = usePaginated(filtered);

  async function handleSave(data: UserSaveData) {
    try {
      if (editing === 'new') {
        await adminCreateUser(data);
        addToast('Benutzer erstellt.');
      } else if (editing) {
        const patch: Parameters<typeof adminUpdateUser>[1] = {
          username: data.username, role: data.role, companyId: data.companyId, displayName: data.displayName,
        };
        if (data.password) patch.password = data.password;
        await adminUpdateUser(editing.id, patch);
        addToast('Benutzer aktualisiert.');
      }
      setEditing(null);
      await onChanged();
    } catch { addToast('Fehler beim Speichern.', 'danger'); }
  }

  async function handleToggleActive(u: SafeUser) {
    try {
      await adminUpdateUser(u.id, { active: !u.active });
      if (u.active) { try { await adminKillUserSessions(u.id); } catch { /* ignore */ } }
      addToast(u.active ? 'Benutzer deaktiviert.' : 'Benutzer aktiviert.');
      await onChanged();
    } catch { addToast('Fehler beim Aktualisieren.', 'danger'); }
  }

  async function handleDelete(u: SafeUser) {
    try {
      await adminKillUserSessions(u.id);
      await adminDeleteUser(u.id);
      addToast('Benutzer gelöscht.');
      setConfirmDelete(null);
      await onChanged();
    } catch { addToast('Fehler beim Löschen.', 'danger'); }
  }

  if (editing) {
    return (
      <UserEditor
        initial={editing === 'new' ? undefined : editing}
        companies={companies}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>+ Benutzer hinzufügen</button>
        <input type="search" className="form-control form-control-sm ms-auto" style={{ maxWidth: 280 }} placeholder="Suche nach Name, Benutzer, Unternehmen…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.83rem' }}>
          <thead style={TH}>
            <tr>
              <th className="ps-3">Anzeigename</th>
              <th>Benutzername</th>
              <th>Rolle</th>
              <th>Unternehmen</th>
              <th>Status</th>
              <th className="text-end pe-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {pag.slice.map((u) => {
              const company = companies.find((c) => c.id === u.companyId);
              const isMe = u.id === currentUser?.id;
              return (
                <tr key={u.id} className={u.active ? '' : 'opacity-50'}>
                  <td className="ps-3 fw-semibold">
                    {u.displayName}
                    {isMe && <span className="badge bg-info-subtle text-info-emphasis ms-2" style={{ fontSize: '0.65rem' }}>Sie</span>}
                  </td>
                  <td>{u.username}</td>
                  <td><span className={`badge ${u.role === 'admin' ? 'bg-primary' : 'bg-secondary'}`}>{u.role === 'admin' ? 'Admin' : 'Benutzer'}</span></td>
                  <td>{company?.name ?? <span className="text-muted">—</span>}</td>
                  <td><span className={`badge ${u.active ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>{u.active ? 'Aktiv' : 'Deaktiviert'}</span></td>
                  <td className="text-end pe-3">
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.72rem' }} onClick={() => setEditing(u)}>Bearbeiten</button>
                      {!isMe && <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.72rem' }} onClick={() => handleToggleActive(u)}>{u.active ? 'Sperren' : 'Aktivieren'}</button>}
                      {!isMe && <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.72rem' }} onClick={() => setConfirmDelete(u)}>Löschen</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {pag.slice.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">{searchQ ? 'Keine Treffer' : 'Keine Benutzer'}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <PaginationBar page={pag.page} totalPages={pag.totalPages} pageSize={pag.pageSize} total={pag.total} onPage={pag.setPage} onPageSize={pag.setPageSize} />

      {confirmDelete && (
        <ConfirmDialog
          title="Benutzer löschen?"
          text={`„${confirmDelete.displayName}" wird gelöscht und alle Sitzungen beendet.`}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}
